import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { station, getBackendUrl, setBackendUrl } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, PlugsConnected, DownloadSimple, Copy } from "@phosphor-icons/react";

const DEFAULT_BACKEND = process.env.REACT_APP_BACKEND_URL;

export const SettingsDialog = ({ open, onOpenChange, state }) => {
  const [x, setX] = useState("");
  const [y, setY] = useState("");
  const [z, setZ] = useState("");
  const [ih, setIh] = useState("");

  const [bridgeUrl, setBridgeUrlLocal] = useState(getBackendUrl());
  const [pingStatus, setPingStatus] = useState(null); // {ok, message}
  const [pinging, setPinging] = useState(false);

  // Initialize local edit buffers ONCE when the dialog opens.
  // Do NOT re-run on every parent state poll (~300 ms) — that would blow
  // away in-progress user edits (paste, typing, etc.).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      wasOpen.current = true;
      if (state) {
        setX(state.station_x?.toFixed(3) ?? "");
        setY(state.station_y?.toFixed(3) ?? "");
        setZ(state.station_z?.toFixed(3) ?? "");
        setIh(state.instrument_height?.toFixed(3) ?? "");
      }
      setBridgeUrlLocal(getBackendUrl());
      setPingStatus(null);
    } else if (!open) {
      wasOpen.current = false;
    }
  }, [open, state]);

  const saveStation = async () => {
    try {
      await station.setup({
        x: parseFloat(x),
        y: parseFloat(y),
        z: parseFloat(z),
        instrument_height: parseFloat(ih),
      });
      toast.success("Station setup updated");
    } catch (e) {
      toast.error("Setup failed");
    }
  };

  const applyBridge = () => {
    setBackendUrl(bridgeUrl);
    toast.success("Backend URL updated");
    // Force a reload so all React Query / polling picks up the new base.
    setTimeout(() => window.location.reload(), 400);
  };

  const useSimulator = () => {
    setBridgeUrlLocal(DEFAULT_BACKEND);
    setBackendUrl("");
    toast.success("Switched to simulator");
    setTimeout(() => window.location.reload(), 400);
  };

  const testConnection = async () => {
    setPinging(true);
    setPingStatus(null);
    try {
      const s = await station.ping(bridgeUrl);
      setPingStatus({
        ok: true,
        message: `${s.model || "OK"} · fw ${s.firmware || "—"}`,
      });
    } catch (e) {
      setPingStatus({
        ok: false,
        message: e.message || "unreachable",
      });
    } finally {
      setPinging(false);
    }
  };

  const isSimulator = bridgeUrl === DEFAULT_BACKEND;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="settings-dialog"
        className="border-[var(--border)] bg-[var(--surface)] text-[var(--text)] max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="tracking-widest">SETTINGS</DialogTitle>
        </DialogHeader>

        {/* Bridge / Backend */}
        <section>
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-[var(--text-dim)]">
              Backend / Bridge
            </div>
            <div
              data-testid="mode-indicator"
              className={`rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                isSimulator
                  ? "border-[var(--amber)] text-[var(--amber)]"
                  : "border-[var(--neon)] text-[var(--neon)]"
              }`}
            >
              {isSimulator ? "SIMULATOR" : "REAL HARDWARE"}
            </div>
          </div>

          <label className="mb-1 block text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
            Bridge URL
          </label>
          <div className="flex gap-2">
            <input
              data-testid="bridge-url-input"
              id="bridge-url-input"
              name="bridgeUrl"
              type="url"
              value={bridgeUrl}
              onChange={(e) => setBridgeUrlLocal(e.target.value)}
              onPaste={(e) => {
                // Ensure clipboard content lands in state even if Radix's
                // focus trap swallowed the default paste on iPad Safari.
                const text = e.clipboardData?.getData("text");
                if (text) {
                  e.preventDefault();
                  setBridgeUrlLocal(text.trim());
                }
              }}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              inputMode="url"
              placeholder="http://192.168.4.1:8765"
              className="readout flex-1 rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--neon)]"
              style={{ WebkitUserSelect: "text", userSelect: "text" }}
            />
            <button
              data-testid="paste-bridge-btn"
              onClick={async () => {
                try {
                  const text = await navigator.clipboard.readText();
                  if (text) {
                    setBridgeUrlLocal(text.trim());
                    toast.success("Pasted from clipboard");
                  }
                } catch (err) {
                  toast.error("Clipboard access denied — allow it in browser settings");
                }
              }}
              className="btn-tac !min-h-[40px] px-3 text-xs"
            >
              PASTE
            </button>
            <button
              data-testid="test-bridge-btn"
              onClick={testConnection}
              disabled={pinging}
              className="btn-tac !min-h-[40px] px-3 text-xs"
            >
              {pinging ? "TESTING…" : "TEST"}
            </button>
          </div>

          {pingStatus && (
            <div
              data-testid="bridge-ping-status"
              className={`mt-2 flex items-center gap-2 text-xs ${
                pingStatus.ok ? "text-[var(--neon)]" : "text-[var(--danger)]"
              }`}
            >
              {pingStatus.ok ? (
                <CheckCircle size={16} weight="bold" />
              ) : (
                <XCircle size={16} weight="bold" />
              )}
              <span className="readout tabular-nums">{pingStatus.message}</span>
            </div>
          )}

          <div className="mt-3 flex gap-2">
            <button
              data-testid="apply-bridge-btn"
              onClick={applyBridge}
              className="btn-tac !min-h-[40px] flex-1 px-3 text-xs"
              data-variant="primary"
            >
              <span className="flex items-center justify-center gap-1">
                <PlugsConnected size={14} weight="bold" /> APPLY & RELOAD
              </span>
            </button>
            <button
              data-testid="use-simulator-btn"
              onClick={useSimulator}
              disabled={isSimulator}
              className="btn-tac !min-h-[40px] flex-1 px-3 text-xs"
            >
              USE SIMULATOR
            </button>
          </div>

          <p className="mt-2 text-xs text-[var(--text-mute)]">
            Point at the local bridge running on your Windows / Linux / Android
            (Termux) device paired to the total station. See{" "}
            <code className="font-mono text-[var(--text-dim)]">/app/bridge/README.md</code>{" "}
            for setup.
          </p>

          <div className="mt-3 rounded-sm border border-[var(--primary)] bg-[var(--surface-2)] p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-[var(--primary)]">
                Windows one-click launcher
              </div>
              <span className="text-[9px] uppercase tracking-widest text-[var(--text-mute)]">
                recommended
              </span>
            </div>
            <p className="mb-2 text-[11px] leading-relaxed text-[var(--text-dim)]">
              Download <code className="font-mono text-[var(--text)]">Start-FabAxis-Bridge.bat</code>
              , double-click it, pick your total-station COM port from the menu, and go.
              The .bat auto-downloads the latest <code className="font-mono">bridge.py</code>{" "}
              and installs any missing Python packages on first run.
            </p>
            <a
              data-testid="download-launcher-btn"
              href={`${DEFAULT_BACKEND}/api/bridge/launcher`}
              download="Start-FabAxis-Bridge.bat"
              className="btn-tac !min-h-[40px] w-full px-3 text-[11px]"
              data-variant="primary"
            >
              <span className="flex items-center justify-center gap-1">
                <DownloadSimple size={14} weight="bold" /> Download Start-FabAxis-Bridge.bat
              </span>
            </a>
            <p className="mt-2 text-[10px] text-[var(--text-mute)]">
              Prerequisite: Python 3.10+ installed (tick "Add Python to PATH" during install).
              The .bat installs everything else automatically.
            </p>
          </div>

          <div className="mt-3 rounded-sm border border-[var(--border)] bg-[var(--surface-2)] p-3">
            <div className="mb-2 text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
              Android tablet — Termux (one-command install)
            </div>
            <p className="mb-2 text-[11px] leading-relaxed text-[var(--text-dim)]">
              Install <b>Termux</b> from <a href="https://f-droid.org/en/packages/com.termux/" target="_blank" rel="noreferrer" className="text-[var(--primary)] underline">F-Droid</a> (not Play Store), open it and paste this line:
            </p>
            <pre className="readout overflow-x-auto rounded-sm bg-black px-3 py-2 text-[11px] leading-relaxed text-[var(--neon)]">
{`curl -sSL ${DEFAULT_BACKEND}/api/bridge/termux-setup | bash`}
            </pre>
            <div className="mt-2 flex gap-2">
              <button
                data-testid="copy-termux-btn"
                onClick={() => {
                  const cmd = `curl -sSL ${DEFAULT_BACKEND}/api/bridge/termux-setup | bash`;
                  navigator.clipboard?.writeText(cmd);
                  toast.success("Termux install command copied");
                }}
                className="btn-tac !min-h-[36px] flex-1 px-3 text-[11px]"
              >
                <span className="flex items-center justify-center gap-1">
                  <Copy size={14} weight="bold" /> Copy one-liner
                </span>
              </button>
              <a
                data-testid="download-bridge-btn"
                href={`${DEFAULT_BACKEND}/api/bridge/download`}
                download="bridge.py"
                className="btn-tac !min-h-[36px] flex-1 px-3 text-[11px]"
              >
                <span className="flex items-center justify-center gap-1">
                  <DownloadSimple size={14} weight="bold" /> bridge.py only
                </span>
              </a>
              <a
                data-testid="download-readme-btn"
                href={`${DEFAULT_BACKEND}/api/bridge/readme`}
                download="BRIDGE_README.md"
                className="btn-tac !min-h-[36px] flex-1 px-3 text-[11px]"
              >
                <span className="flex items-center justify-center gap-1">
                  <DownloadSimple size={14} weight="bold" /> README
                </span>
              </a>
            </div>
            <p className="mt-2 text-[10px] leading-relaxed text-[var(--text-mute)]">
              The installer sets up Python + all packages, downloads bridge.py, creates a{" "}
              <code className="font-mono text-[var(--text)]">start-fabaxis</code>{" "}
              command, and launches the bridge. After first run just type{" "}
              <code className="font-mono text-[var(--text)]">start-fabaxis</code>{" "}
              in Termux to start it again.
            </p>
          </div>
        </section>

        {/* Station setup */}
        <section className="border-t border-[var(--border)] pt-4">
          <div className="mb-2 text-[11px] uppercase tracking-widest text-[var(--text-dim)]">
            Station setup (m)
          </div>
          <div className="grid grid-cols-4 gap-2">
            <Num label="Easting (X)" testid="setup-x-input" value={x} onChange={setX} />
            <Num label="Northing (Y)" testid="setup-y-input" value={y} onChange={setY} />
            <Num label="Elev. (Z)" testid="setup-z-input" value={z} onChange={setZ} />
            <Num label="Instr. Hgt" testid="setup-ih-input" value={ih} onChange={setIh} />
          </div>
          <div className="mt-3 flex justify-end">
            <button
              data-testid="settings-save-btn"
              onClick={saveStation}
              className="btn-tac !min-h-[40px] px-4 text-xs"
              data-variant="primary"
            >
              SAVE STATION
            </button>
          </div>
        </section>

        {/* Instrument info */}
        <section className="border-t border-[var(--border)] pt-4">
          <div className="mb-2 text-[11px] uppercase tracking-widest text-[var(--text-dim)]">
            Instrument
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 readout text-sm tabular-nums">
            <span className="text-[var(--text-dim)]">Model</span>
            <span data-testid="settings-model">{state?.model ?? "—"}</span>
            <span className="text-[var(--text-dim)]">Serial</span>
            <span data-testid="settings-serial">{state?.serial ?? "—"}</span>
            <span className="text-[var(--text-dim)]">Firmware</span>
            <span data-testid="settings-firmware">{state?.firmware ?? "—"}</span>
          </div>
        </section>

        <div className="flex items-center justify-between pt-3">
          <div className="flex items-center gap-2 opacity-60">
            <img src="/wilson-design-logo.png" alt="Wilson Design" className="h-6 w-auto" />
            <span className="text-[10px] tracking-widest text-[var(--text-mute)]">
              A WILSON DESIGN PRODUCT
            </span>
          </div>
          <button
            data-testid="settings-close-btn"
            onClick={() => onOpenChange(false)}
            className="btn-tac !min-h-[40px] px-4 text-xs"
          >
            CLOSE
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Num = ({ label, testid, value, onChange }) => (
  <label className="block">
    <span className="mb-1 block text-[10px] uppercase tracking-widest text-[var(--text-dim)]">{label}</span>
    <input
      data-testid={testid}
      type="number"
      step="0.001"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="readout w-full rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-2 py-2 text-sm tabular-nums text-[var(--text)] focus:border-[var(--neon)]"
    />
  </label>
);
